import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid';

import { AppDataGridColumnSettingsButton, Page, ReportTable } from 'components';
import { useMisc, useSession } from 'store';
import { t } from 'i18n';
import { callApi, download, formatFieldName } from 'utils';

// some common columns
// the actual used columns are specified dynamically in dataUrl
const extraReportColumnLabels = {
  org: '合作方',
  start_time: '开始日期',
  end_time: '结束日期',
  end_month: '结束月份',
  month: '月份',
  date: '日期',
  grpname: '订单名称',
  parent: '订单ID',
  scheduled_imp: '排期曝光',
  actual_imp: '实际曝光',
  imp: '结算曝光',
  scheduled_clk: '排期点击',
  actual_clk: '实际点击',
  clk: '结算点击',
};
const settlementCostColumnLabels = {
  cost: '结算值',
  total_cost: '排期值',
};
const reportColumnLabelOverrides = {
  'total-org-cost': settlementCostColumnLabels,
  'monthly-cost': settlementCostColumnLabels,
  'monthly-cost-details': settlementCostColumnLabels,
};
const columns = {
  org: { minWidth: 96, flex: 0.7 },
  start_time: { minWidth: 112, flex: 0.8 },
  end_time: { minWidth: 112, flex: 0.8 },
  end_month: { minWidth: 96, flex: 0.75 },
  month: { minWidth: 96, flex: 0.75 },
  date: { minWidth: 104, flex: 0.8 },
  grpname: { minWidth: 240, flex: 3, truncate: true },
  parent: { minWidth: 84, flex: 0.65 },
  scheduled_imp: { numeric: true, minWidth: 104, flex: 0.8 },
  actual_imp: { numeric: true, minWidth: 104, flex: 0.8 },
  imp: { numeric: true, minWidth: 104, flex: 0.8 },
  scheduled_clk: { numeric: true, minWidth: 104, flex: 0.8 },
  actual_clk: { numeric: true, minWidth: 104, flex: 0.8 },
  clk: { numeric: true, minWidth: 104, flex: 0.8 },
  cost: { numeric: true, minWidth: 96, flex: 0.75 },
  total_cost: { numeric: true, minWidth: 96, flex: 0.75 },
};
const compactSettlementMetricSizing = {
  scheduled_imp: { minWidth: 80, flex: 0.72 },
  actual_imp: { minWidth: 80, flex: 0.72 },
  imp: { minWidth: 80, flex: 0.72 },
  scheduled_clk: { minWidth: 80, flex: 0.72 },
  actual_clk: { minWidth: 80, flex: 0.72 },
  clk: { minWidth: 80, flex: 0.72 },
  cost: { minWidth: 80, flex: 0.72 },
  total_cost: { minWidth: 80, flex: 0.72 },
};
const reportColumnSizingOverrides = {
  'total-org-cost': {
    org: { minWidth: 96, flex: 0.8 },
    start_time: { minWidth: 88, flex: 0.75 },
    end_time: { minWidth: 88, flex: 0.75 },
    grpname: { minWidth: 180, flex: 2.4 },
    parent: { minWidth: 72, flex: 0.6 },
    ...compactSettlementMetricSizing,
  },
  'monthly-cost': {
    org: { minWidth: 96, flex: 0.8 },
    end_month: { minWidth: 88, flex: 0.75 },
    ...compactSettlementMetricSizing,
  },
  'monthly-cost-details': {
    org: { minWidth: 96, flex: 0.8 },
    parent: { minWidth: 72, flex: 0.6 },
    grpname: { minWidth: 180, flex: 2.4 },
    ...compactSettlementMetricSizing,
  },
};
const defaultRowsPerPage = 100;
const defaultDateRangeDays = 60;
const defaultExtraReportPath = '/reports/extra/total-org-cost';
const reportDownloadBasenames = {
  'total-org-cost': '合作方总花费',
  'monthly-cost-details': '结算月报表详情',
};
const dateFilterReports = new Set(['total-org-cost']);
const monthFilterReports = new Set(['monthly-cost', 'monthly-cost-details']);
const orgFilterReports = new Set(['monthly-cost-details']);
const filterItemSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};
const filterToolbarSx = {
  width: '100%',
  flexWrap: 'wrap',
};
const dateFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1.2 1 300px'},
  minWidth: {xs: '100%', sm: 300},
};
const filterActionsSx = {
  display: 'flex',
  flex: {xs: '1 1 100%', sm: '0 0 auto'},
  gap: 1,
  whiteSpace: 'nowrap',
};
const datePickerSx = {
  flex: '1 1 120px',
  minWidth: 0,
  '& .MuiInputBase-input': {p: '6px'},
};
const monthPickerSx = {
  width: 136,
  '& .MuiInputBase-input': {p: '6px'},
};
const orgSelectSx = {
  minWidth: 128,
  '& .MuiSelect-select': {py: '6px'},
};
const filterButtonSx = {
  minWidth: 88,
  flexShrink: 0,
};
const formatPrice = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : value;
};
const getExtraReportColumnLabel = (dim, col) => (
  reportColumnLabelOverrides[dim]?.[col] || extraReportColumnLabels[col] || formatFieldName(col)
);
const getExtraReportColumns = (dim) => {
  const sizingOverrides = reportColumnSizingOverrides[dim];
  if (!sizingOverrides) return columns;

  return Object.fromEntries(Object.entries(columns).map(([key, column]) => [
    key,
    sizingOverrides[key] ? {...column, ...sizingOverrides[key]} : column,
  ]));
};
const getDefaultDateRange = () => {
  const endDate = dayjs();
  return {
    start_date: endDate.subtract(defaultDateRangeDays - 1, 'day'),
    end_date: endDate,
  };
};
const getDateFilterFromParams = (searchParams) => {
  const defaults = getDefaultDateRange();
  return {
    start_date: searchParams.get('start') ? dayjs(searchParams.get('start')) : defaults.start_date,
    end_date: searchParams.get('end') ? dayjs(searchParams.get('end')) : defaults.end_date,
  };
};
const getDefaultMonth = () => dayjs().subtract(1, 'month').startOf('month');
const getMonthFilterFromParams = (searchParams) => {
  const month = searchParams.get('month');
  return /^\d{4}-\d{2}$/.test(month || '') ? dayjs(`${month}-01`) : getDefaultMonth();
};
const formatDateParam = (value) => (
  value?.isValid?.() ? value.format('YYYY-MM-DD') : ''
);
const buildExtraReportParams = (searchParams, dim, {pagination=true} = {}) => {
  const params = new URLSearchParams(searchParams);

  if (dateFilterReports.has(dim)) {
    const defaults = getDefaultDateRange();
    params.get('start') || params.set('start', defaults.start_date.format('YYYY-MM-DD'));
    params.get('end') || params.set('end', defaults.end_date.format('YYYY-MM-DD'));
  }

  if (monthFilterReports.has(dim)) {
    params.get('month') || params.set('month', getDefaultMonth().format('YYYY-MM'));
  }

  if (!orgFilterReports.has(dim)) {
    params.delete('org');
  }

  if (pagination) {
    params.set('limit', defaultRowsPerPage);
  } else {
    params.delete('page');
    params.delete('limit');
  }

  return params;
};

const ExtraReports = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportGridApiRef = useGridApiRef();
  const misc = useMisc((state) => state.data);
  const session = useSession((state) => state.session);
  const [title, setTitle] = useState('Extra Reports');
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(() => getDateFilterFromParams(searchParams));
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);

  const dim = location.pathname.slice(15); // /reports/extra/abc...
  const page = searchParams.get('page')|0;
  const limit = defaultRowsPerPage;
  const searchParamsKey = searchParams.toString();
  const downloadBasename = reportDownloadBasenames[dim];
  const hasDateFilter = dateFilterReports.has(dim);
  const hasMonthFilter = monthFilterReports.has(dim);
  const hasOrgFilter = session.role === 'admin' && orgFilterReports.has(dim);
  const monthFilter = getMonthFilterFromParams(searchParams);
  const orgFilter = searchParams.get('org') || '';
  const reportColumns = useMemo(() => getExtraReportColumns(dim), [dim]);

  useEffect(() => {
    if (!searchParams.has('limit')) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('limit');
      return next;
    });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!hasDateFilter) return;
    setFilter(getDateFilterFromParams(searchParams));
  }, [hasDateFilter, searchParams, searchParamsKey]);

  useEffect(() => {
    if (!dim) return;

    let canceled = false;
    setData(null);

    const params = buildExtraReportParams(searchParams, dim);
    callApi(`/reports/${dim}?${params.toString()}`)
      .then((res) => {
        if (canceled) return;

        setData(res);
        setTitle(res.title || 'Extra Reports');
      })
      .catch(console.error);

    return () => { canceled = true; };
  }, [dim, limit, searchParams, searchParamsKey]);

  const getColumnLabel = useCallback((col) => (
    getExtraReportColumnLabel(dim, col)
  ), [dim]);
  const renderCell = useCallback((col, row) => {
    if (col === 'org') return misc.org[row[col]] || row[col];
    if (col === 'cost' || col === 'total_cost') return formatPrice(row[col]);
    return row[col];
  }, [misc]);
  const getRowKey = useCallback((row) => (
    row.id || [dim, row.org, row.parent, row.grpname, row.start_time, row.end_time, row.end_month, row.date].join(':')
  ), [dim]);
  const handlePagination = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'page') {
        value > 0 ? next.set(key, value) : next.delete(key);
      } else {
        value !== defaultRowsPerPage ? next.set(key, value) : next.delete(key);
        next.delete('page');
      }
      return next;
    });
  }, [setSearchParams]);
  const changeDate = useCallback((name, val) => setFilter(prev => ({...prev, [name]: val})), []);
  const submitFilter = useCallback((ev) => {
    ev?.preventDefault();
    const sp = new URLSearchParams(searchParams);
    sp.delete('page');
    sp.delete('limit');

    const startDate = formatDateParam(filter.start_date);
    const endDate = formatDateParam(filter.end_date);
    startDate ? sp.set('start', startDate) : sp.delete('start');
    endDate ? sp.set('end', endDate) : sp.delete('end');

    setSearchParams(sp);
  }, [filter, searchParams, setSearchParams]);
  const resetFilter = useCallback(() => {
    const defaults = getDefaultDateRange();
    const nextFilter = {...defaults};
    setFilter(nextFilter);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      next.delete('limit');
      next.set('start', defaults.start_date.format('YYYY-MM-DD'));
      next.set('end', defaults.end_date.format('YYYY-MM-DD'));
      return next;
    });
  }, [setSearchParams]);
  const changeMonth = useCallback((val) => {
    if (!val?.isValid?.()) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      next.delete('limit');
      next.set('month', val.format('YYYY-MM'));
      return next;
    });
  }, [setSearchParams]);
  const changeOrg = useCallback((ev) => {
    const org = ev.target.value;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      next.delete('limit');
      org ? next.set('org', org) : next.delete('org');
      return next;
    });
  }, [setSearchParams]);
  const exportExtraReport = useCallback(() => {
    const params = buildExtraReportParams(searchParams, dim, {pagination: false});
    params.set('type', 'extra');
    params.set('dim', dim);
    download(`/reports/export?${params.toString()}`, data?.title || downloadBasename || dim);
  }, [data?.title, dim, downloadBasename, searchParams]);
  const columnSettingsButtonId = `extra-report-${dim}-column-settings-button`;
  const columnSettingsPanelId = `extra-report-${dim}-column-settings-panel`;

  if (!dim) {
    return <Navigate to={defaultExtraReportPath} replace />;
  }

  return (
    <Page title={title} styles={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Stack spacing={1} sx={{height: '100%', minHeight: 0, p: 2, pt: 1}}>
        {hasDateFilter ? (
          <Box component="form" onSubmit={submitFilter} sx={{flexShrink: 0}}>
            <Grid container spacing={1} alignItems="center" sx={filterToolbarSx}>
              <Grid item sx={dateFilterItemSx}>
                <Typography sx={{mr: 0.5, flexShrink: 0}}>创建时间:</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0}}>
                  <DatePicker
                    sx={datePickerSx}
                    value={filter.start_date}
                    views={['year', 'month', 'day']}
                    onChange={val => changeDate('start_date', val)}
                    dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                  />
                  <Typography component="span" sx={{flexShrink: 0}}>~</Typography>
                  <DatePicker
                    sx={datePickerSx}
                    value={filter.end_date}
                    views={['year', 'month', 'day']}
                    minDate={filter.start_date}
                    onChange={val => changeDate('end_date', val)}
                    dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                  />
                </Box>
              </Grid>
              <Grid item sx={filterActionsSx}>
                <Button
                  type="submit"
                  sx={filterButtonSx}
                  variant="outlined"
                  size="small"
                >搜索</Button>
                <Button
                  type="button"
                  sx={filterButtonSx}
                  variant="outlined"
                  size="small"
                  onClick={resetFilter}
                >重置</Button>
                {downloadBasename && (
                  <Button
                    type="button"
                    sx={filterButtonSx}
                    variant="outlined"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={exportExtraReport}
                  >下载</Button>
                )}
                <AppDataGridColumnSettingsButton
                  apiRef={reportGridApiRef}
                  buttonId={columnSettingsButtonId}
                  panelId={columnSettingsPanelId}
                  label={t('Column Settings')}
                  ref={setColumnSettingsAnchorEl}
                  type="button"
                />
              </Grid>
            </Grid>
          </Box>
        ) : hasMonthFilter ? (
          <Stack direction="row" justifyContent="space-between" sx={{alignItems: 'center', flexShrink: 0, gap: 1, flexWrap: 'wrap'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap'}}>
              <Typography sx={{flexShrink: 0}}>结算月份:</Typography>
              <DatePicker
                sx={monthPickerSx}
                value={monthFilter}
                views={['year', 'month']}
                openTo="month"
                format="YYYY-MM"
                onChange={changeMonth}
              />
              {hasOrgFilter && (
                <>
                  <Typography sx={{ml: 1, flexShrink: 0}}>合作方:</Typography>
                  <Select
                    name="org"
                    value={orgFilter}
                    size="small"
                    onChange={changeOrg}
                    displayEmpty
                    sx={orgSelectSx}
                  >
                    <MenuItem value="">全部合作方</MenuItem>
                    {Object.entries(misc.org || {}).map(([val, display]) => (
                      <MenuItem value={val} key={val}>{display}</MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </Box>
            <Stack direction="row" sx={{alignItems: 'center', flexShrink: 0, gap: 1, flexWrap: 'wrap'}}>
              {downloadBasename && (
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={exportExtraReport}
                >下载</Button>
              )}
              <AppDataGridColumnSettingsButton
                apiRef={reportGridApiRef}
                buttonId={columnSettingsButtonId}
                panelId={columnSettingsPanelId}
                label={t('Column Settings')}
                ref={setColumnSettingsAnchorEl}
                type="button"
              />
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-end" sx={{alignItems: 'center', flexShrink: 0, gap: 1}}>
            {downloadBasename && (
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<GetAppIcon />}
                onClick={exportExtraReport}
              >下载</Button>
            )}
            <AppDataGridColumnSettingsButton
              apiRef={reportGridApiRef}
              buttonId={columnSettingsButtonId}
              panelId={columnSettingsPanelId}
              label={t('Column Settings')}
              ref={setColumnSettingsAnchorEl}
              type="button"
            />
          </Stack>
        )}
        <ReportTable
          apiRef={reportGridApiRef}
          data={data}
          columns={reportColumns}
          getColumnLabel={getColumnLabel}
          renderCell={renderCell}
          getCellTitle={(col, row) => row[col]}
          getRowKey={getRowKey}
          page={page}
          rowsPerPage={limit}
          onPagination={handlePagination}
          showColumnSettings
          columnSettingsTableId={`report-extra-${dim}`}
          columnSettingsUserId={session?.uid}
          columnSettingsAnchorEl={columnSettingsAnchorEl}
        />
      </Stack>
    </Page>
  );
};

export default ExtraReports;

export const __testing = {
  getExtraReportColumnLabel,
  getExtraReportColumns,
};
